import { useRef, useEffect, useCallback } from 'react';
import { Drawer } from '@mui/material';

import { useOnClickOutside } from '@flexo/hooks';
import { UpdateCredentialsForm } from '../update-credentials-form/update-credentials-form';

import styles from './right-drawer.module.scss';
import {TotalEstimatedBenefitsDrawerContent} from './drawer-contents/total-estimated-benefits';
import {CommunityTotalEstimatedBenefitsDrawerContent} from './drawer-contents/community-total-estimated-benefits';
import {CommunityCo2DrawerContent} from './drawer-contents/community-co2';
import {CommunitySelfSufficiencyDrawerContent} from './drawer-contents/community-self-sufficiency';
import {TotalCostsDrawerContent} from './drawer-contents/total-costs';
import {default as PaymentsListDrawerContent} from './drawer-contents/payments-list';
import {default as EndUserInvoicesDrawerContent} from './drawer-contents/enduser-invoices';
import { EsriMapComponent } from '@flexo/atoms';
import { useDispatch, useSelector } from 'react-redux';
import { RIGHT_DRAWER } from '@flexo/reducers';
import { FeedbackFormDrawerContent } from './drawer-contents/feedback-form-drawer-content';
import { HorizontalChartDrawerContent } from './drawer-contents/horizontal-chart-content';
import { InformationsDrawerContent } from './drawer-contents/informations-drawer-content';
import { AdminAddNewUserDrawerContent } from './drawer-contents/admin-add-new-user-drawer-content';
import { AdminUpdateUserDrawerContent } from './drawer-contents/admin-update-user-drawer-content';

export const RightDrawer = () => {
  const drawerRef = useRef(null);
  const dispatch = useDispatch();
  const rightDrawer = useSelector((state: any) => state.rightDrawer);

  const closedByBackRef = useRef(false);
  const openedAtPathRef = useRef('');

  const hideDrawer = useCallback(() => {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }, [dispatch]);

  // Close drawer on browser back button
  useEffect(() => {
    if (rightDrawer.show) {
      closedByBackRef.current = false;
      openedAtPathRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.history.pushState({ ...(window.history.state || {}), drawer: true }, '');

      const handlePopState = () => {
        closedByBackRef.current = true;
        hideDrawer();
      };

      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
        const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const isDrawerHistoryEntry = window.history.state?.drawer === true;
        if (
          !closedByBackRef.current &&
          isDrawerHistoryEntry &&
          currentPath === openedAtPathRef.current
        ) {
          window.history.back();
        }
      };
    }
  }, [rightDrawer.show, hideDrawer]);

  function isChildOf(event) {
    const parentDiv = document.getElementById('right-drawer');
    let targetElement = event.target; // L'elemento che è stato cliccato

    // Itera attraverso i genitori dell'elemento target fino a raggiungere il body.
    // FLEXO-6054: fermarsi anche se la catena arriva a null — un target staccato dal
    // DOM (rimosso tra click e listener) o un click sopra <body> faceva superare il
    // body e chiamare `null.parentNode` → "null is not an object (evaluating 'n.parentNode')".
    while (targetElement && targetElement !== document.body) {
      if (targetElement === parentDiv) {
        return true; // Il target è figlio del parentDiv
      }
      targetElement = targetElement.parentNode; // Vai al genitore dell'elemento target
    }

    return false; // Il target non è figlio del parentDiv
  }

  function checkBackdropClose(ev: any) {
    const classContent = (ev?.target?.className && ev.target.className) || '';
    try {
      if (
        rightDrawer.show === true &&
        classContent?.includes('MuiBackdrop-root') === true &&
        isChildOf(ev) === true
      ) {
        dispatch(RIGHT_DRAWER.HideDrawer());
      }
    } catch (error) {
      console.warn('error', error);
    }
    
  }

  useOnClickOutside(drawerRef, (ev: any) => {
    if (isChildOf(ev) === false) {
      return;
    }

    if (rightDrawer.show === true) {
      dispatch(RIGHT_DRAWER.HideDrawer());
    }
  });

  const getDrawerContent = () => {
    const requiredContent = rightDrawer?.type || '';

    let content = <></>;

    switch (requiredContent) {
      case 'CREDENTIALS_UPDATE_FORM':
        content = <UpdateCredentialsForm />;
      break;
      case 'ADMIN_ADD_NEW_USER':
        content = <AdminAddNewUserDrawerContent />;
      break;
      case 'ADMIN_UPDATE_USER':
        content = <AdminUpdateUserDrawerContent />;
      break;
      case 'COMMUNITY_TOTAL_COSTS':
      case 'MEMBER_TOTAL_COSTS':
      case 'SITE_TOTAL_COSTS':
        content = <TotalCostsDrawerContent drawerConfig={ rightDrawer.drawerConfig }/>;
      break;
      case 'COMMUNITY_TOTAL_ESTIMATED_BENEFITS':
        content = <CommunityTotalEstimatedBenefitsDrawerContent drawerConfig={ rightDrawer.drawerConfig }/>;
        break;
      case 'COMMUNITY_CO2':
        content = <CommunityCo2DrawerContent drawerConfig={ rightDrawer.drawerConfig }/>;
        break;
      case 'COMMUNITY_SELF_SUFFICIENCY':
        content = <CommunitySelfSufficiencyDrawerContent drawerConfig={ rightDrawer.drawerConfig }/>;
        break;
      case 'MEMBER_TOTAL_ESTIMATED_BENEFITS':
      case 'SITE_TOTAL_ESTIMATED_BENEFITS':
        content = <TotalEstimatedBenefitsDrawerContent drawerConfig={ rightDrawer.drawerConfig }/>;
        break;
      case 'COMMUNITY_PAYMENTS_LIST':
        content = <PaymentsListDrawerContent type={'overview'} drawerConfig={rightDrawer.drawerConfig} />;
        break;
      case 'ENDUSER_INVOICES':
        // FLEXO-6075 #2: end-user invoices as the mobile single-list (Billing), not the table.
        content = <EndUserInvoicesDrawerContent drawerConfig={rightDrawer.drawerConfig} />;
        break;
      case 'INVOICES_PERIOD_DETAILS':
        content = <PaymentsListDrawerContent type={'details'} drawerConfig={ rightDrawer.drawerConfig }/>;
        break;
        case 'FEEDBACK_FORM':
        content = <FeedbackFormDrawerContent />;
        break;
      case 'INFORMATIONS_PAGE':
        content = <InformationsDrawerContent />;
        break;
      case 'EXPANDED_MAP':
        content = (
          <EsriMapComponent
            {...{
              wrapper: 'drawer',
              env: 'web',
            }}
          />
        );
        break;
      case 'HORIZONTAL_CHART':
        content = <HorizontalChartDrawerContent drawerConfig={ rightDrawer.drawerConfig }/>;
        break;
      default:
        content = <></>;
        break;
    }

    return content;
  };

  function getDrawerClassName() {
    if (rightDrawer?.type === 'INVOICES_PERIOD_DETAILS' || rightDrawer?.type === 'COMMUNITY_PAYMENTS_LIST') {
      return styles.RightDrawer__Invoices;
    }
    if (rightDrawer?.type === 'EXPANDED_MAP') {
      return styles.RightDrawer__Map;
    }
    if (rightDrawer?.type === 'FEEDBACK_FORM') {
      return styles.RightDrawer__Feedback__Form;
    }
    if (rightDrawer?.type === 'HORIZONTAL_CHART') {
      return styles.RightDrawer__HorizontalChart;
    }
    if (rightDrawer?.type === 'INFORMATIONS_PAGE') {
      return styles.RightDrawer__Informations;
    }
    if (rightDrawer?.type === 'ADMIN_ADD_NEW_USER') {
      return styles.RightDrawer__AdminUser;
    }
    if (rightDrawer?.type === 'ADMIN_UPDATE_USER') {
      return styles.RightDrawer__AdminUser;
    }
    return styles.RightDrawer;
  }

  return (
    <div
      ref={drawerRef}
      id="right-drawer"
      onClick={(ev: any) => checkBackdropClose(ev)}
    >
      <Drawer
        open={rightDrawer.show}
        anchor={'right'}
        disablePortal={true}
        disableEnforceFocus
        disableRestoreFocus
        onClose={hideDrawer}
      >
        {/* FLEXO-6089: role="dialog" + aria-modal so screen readers announce the
            drawer as a modal dialog and trap virtual cursor inside it. */}
        <div
          className={getDrawerClassName()}
          role="dialog"
          aria-modal="true"
          aria-label="Detail drawer"
        >
          {getDrawerContent()}
        </div>
      </Drawer>
    </div>
  );
};

export default RightDrawer;
